import { KeyRound } from 'lucide-react';
import type { FeatureKey } from '@/constants/features';
import { permissionGroupsFor, permissionGroupTitle, type Permission } from '@/constants/permissions';
import { cn } from '@/lib/utils';

/** One area of the product, and how much of it a role reaches. */
export interface GroupCoverage {
  title: string;
  held: number;
  total: number;
}

/**
 * How much of each area a role reaches.
 *
 * Exported because the card prints the same figures under the meter, and two
 * places counting the same thing separately is two places to disagree
 * (CLAUDE.md §3).
 */
export function coverageOf(
  permissions: Permission[],
  features?: readonly FeatureKey[],
  featureNames?: Partial<Record<FeatureKey, string>>,
): GroupCoverage[] {
  const held = new Set(permissions);

  // The pages this school has, under their catalogue names — as the sidebar.
  return permissionGroupsFor(features).map((group) => ({
    title: permissionGroupTitle(group, featureNames),
    held: group.permissions.filter((permission) => held.has(permission)).length,
    total: group.permissions.length,
  }));
}

export interface RoleAccessMeterProps {
  coverage: GroupCoverage[];
  /** Every permission this role holds, and every one there is. */
  held: number;
  total: number;
  className?: string;
}

/**
 * A role's reach, as one strip.
 *
 * The card used to print every permission as its own badge, so a role with
 * thirty of them was thirty identical chips — a wall nobody reads, and no way
 * to tell two roles apart without comparing the walls word by word.
 *
 * This is the same information as a *shape*: one segment per area of the
 * product, filled by how much of that area the role reaches. Full, part and
 * none are three obviously different marks, so two roles side by side can be
 * compared at a glance, and a role holding everything looks unmistakably unlike
 * one holding two things.
 *
 * Every segment is titled and the figures are printed under it as words, so
 * nothing here is carried by colour or by width alone.
 */
export function RoleAccessMeter({ coverage, held, total, className }: RoleAccessMeterProps) {
  const share = total > 0 ? Math.round((held / total) * 100) : 0;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
          Reach
        </p>
        <p className="flex items-center gap-1.5 text-[11px] font-bold tabular-nums text-slate-500 dark:text-slate-400">
          <KeyRound className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
          <span>
            <span className="font-black text-slate-800 dark:text-slate-100">{held}</span> of {total}{' '}
            permissions
          </span>
          <span className="text-slate-400 dark:text-slate-500">· {share}%</span>
        </p>
      </div>

      {/* One segment per area, sized by how many permissions that area has, so
          a wide segment is a wide area rather than an important one. */}
      <div
        className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full"
        role="img"
        aria-label={`Reaches ${held} of ${total} permissions, across ${
          coverage.filter((group) => group.held > 0).length
        } of ${coverage.length} areas`}
      >
        {coverage.map((group) => {
          const isFull = group.held === group.total;
          const isPart = group.held > 0 && !isFull;

          return (
            <span
              key={group.title}
              style={{ flexGrow: group.total }}
              title={`${group.title} — ${group.held} of ${group.total}`}
              className={cn(
                'relative h-full overflow-hidden rounded-sm transition-colors',
                isFull && 'bg-gradient-to-b from-blue-500 to-indigo-600',
                isPart && 'bg-blue-200 dark:bg-blue-900/70',
                !isPart && !isFull && 'bg-slate-100 dark:bg-slate-800',
              )}
            >
              {/* A partly held area is drawn as its own fraction, so "two of
                  four" is visibly less than "three of four". */}
              {isPart && (
                <span
                  className="absolute inset-y-0 left-0 bg-gradient-to-b from-blue-500 to-indigo-600"
                  style={{ width: `${(group.held / group.total) * 100}%` }}
                />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
